/**
 * ChatPage Component — "The Guide"
 * 
 * Visual novel-style character experience with:
 * - Full-body character illustration with mood/pose support
 * - Different poses for: neutral, thinking, happy, concerned
 * - Character reacts to conversation context
 * - Editorial/archive aesthetic (not AI chatbot style)
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, Plus, X, BookOpen, Feather, Download } from 'lucide-react';
import { Pulse } from '../components/Animations';
import { useChat } from '../contexts/ChatContext';

const API_BASE = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';

// =============================================================================
// CHARACTER ASSET CONFIGURATION
// Place images in: frontend/public/images/guide/
// =============================================================================
const CHARACTER_POSES = {
    neutral: '/images/guide/neutral.png',      // Default standing pose
    thinking: '/images/guide/thinking.png',    // Hand on chin, contemplating
    happy: '/images/guide/happy.png',          // Slight smile, welcoming  
    explaining: '/images/guide/explaining.png', // Gesturing, teaching
    concerned: '/images/guide/concerned.png',  // Sympathetic expression
    welcome: '/images/guide/welcome.png',      // Open arms, inviting
};

// Header avatar (circular crop)
const GUIDE_AVATAR = '/images/guide/avatar.png';

// Fallback emoji if images not loaded
const FALLBACK_EMOJI = '🕯️';

// =============================================================================
// CHARACTER COMPONENTS
// =============================================================================

// Circular avatar for header
function GuideAvatar({ size = 'md' }) {
    const [imgError, setImgError] = useState(false);
    const sizeClasses = {
        sm: 'w-8 h-8 text-lg',
        md: 'w-12 h-12 text-2xl',
    };

    return (
        <div className={`${sizeClasses[size]} rounded-full overflow-hidden border-2 border-wine-500/30 bg-gradient-to-br from-wine-900/30 to-charcoal-900 flex items-center justify-center`}>
            {!imgError ? (
                <img
                    src={GUIDE_AVATAR}
                    alt="The Guide"
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                />
            ) : (
                <span>{FALLBACK_EMOJI}</span>
            )}
        </div>
    );
}

// Full-body character illustration with mood support
function GuideCharacter({ mood = 'neutral', className = '' }) {
    const [imgError, setImgError] = useState(false);
    const [currentPose, setCurrentPose] = useState(mood);

    // Animate pose changes
    useEffect(() => {
        setCurrentPose(mood);
    }, [mood]);

    const imageSrc = CHARACTER_POSES[currentPose] || CHARACTER_POSES.neutral;

    if (imgError) {
        return (
            <div className={`flex flex-col items-center justify-center ${className}`}>
                <div className="text-8xl opacity-40 mb-4">{FALLBACK_EMOJI}</div>
                <p className="text-xs text-gray-600 italic">Character images not found</p>
                <p className="text-xs text-gray-700">Place images in /public/images/guide/</p>
            </div>
        );
    }

    return (
        <div className={`relative transition-all duration-500 ${className}`}>
            <img
                src={imageSrc}
                alt={`The Guide - ${mood}`}
                className="h-full w-auto object-contain drop-shadow-2xl transition-opacity duration-300"
                onError={() => setImgError(true)}
            />
            {/* Subtle ambient glow */}
            <div className="absolute inset-0 -z-10 bg-gradient-radial from-wine-500/10 via-transparent to-transparent blur-3xl scale-150" />
        </div>
    );
}

// =============================================================================
// MAIN CHAT PAGE
// =============================================================================

function ChatPage() {
    const {
        tabs,
        activeTabId,
        setActiveTab,
        createTab,
        closeTab,
        addMessage,
        updateLastMessage,
        getActiveTab
    } = useChat();

    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [characterMood, setCharacterMood] = useState('welcome');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    const activeTab = getActiveTab();

    // Determine character mood based on conversation state
    useEffect(() => {
        if (!activeTab?.messages || activeTab.messages.length === 0) {
            setCharacterMood('welcome');
        } else if (isLoading) {
            setCharacterMood('thinking');
        } else {
            // Check last message content for mood hints
            const lastMsg = activeTab.messages[activeTab.messages.length - 1];
            if (lastMsg?.error) {
                setCharacterMood('concerned');
            } else if (lastMsg?.role === 'assistant') {
                setCharacterMood('explaining');
            } else {
                setCharacterMood('neutral');
            }
        }
    }, [activeTab?.messages, isLoading]);

    // Handle URL query param
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const query = params.get('q');
        if (query && activeTab?.messages.length === 0) {
            window.history.replaceState({}, '', '/chat');
            sendMessageWithContent(query);
        }
    }, [location.search, activeTab?.id]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [tabs, activeTabId]);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const sendMessageWithContent = async (content) => {
        if (!content.trim() || isLoading) return;

        const userMessage = content.trim();
        setInput('');
        setError(null);
        setCharacterMood('thinking');

        addMessage({ role: 'user', content: userMessage });
        addMessage({ role: 'assistant', content: '', loading: true });

        setIsLoading(true);

        try {
            const history = (activeTab?.messages || []).map(m => ({
                role: m.role,
                content: m.content
            }));

            const response = await fetch(`${API_BASE}/api/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: userMessage,
                    history: history.slice(-10)
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to get response');
            }

            setCharacterMood('happy');
            updateLastMessage({
                content: data.message,
                loading: false,
                model: data.model,
                context: data.context
            });

            // Return to explaining after a moment
            setTimeout(() => setCharacterMood('explaining'), 1500);

        } catch (err) {
            console.error('Chat error:', err);
            setCharacterMood('concerned');
            updateLastMessage({
                content: 'The archives are temporarily unavailable. Please try again.',
                loading: false,
                error: true
            });
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const sendMessage = () => sendMessageWithContent(input);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const hasMessages = activeTab?.messages?.length > 0;

    // Generate and download transcript with TLDR at top
    const downloadTranscript = () => {
        if (!activeTab?.messages || activeTab.messages.length === 0) return;

        const lines = [];
        const userQuestions = [];

        // Collect all Q&A pairs
        for (let i = 0; i < activeTab.messages.length; i++) {
            const msg = activeTab.messages[i];
            if (msg.role === 'user') {
                userQuestions.push(msg.content);
            }
        }

        // Build TLDR section
        lines.push('# Chat Transcript from Pleasance.app');
        lines.push(`Session: ${activeTab.title}`);
        lines.push(`Downloaded: ${new Date().toLocaleString()}`);
        lines.push('');
        lines.push('---');
        lines.push('');
        lines.push('## TL;DR - Topics Discussed');
        lines.push('');
        userQuestions.forEach((q, i) => {
            lines.push(`${i + 1}. ${q}`);
        });
        lines.push('');
        lines.push('---');
        lines.push('');
        lines.push('## Full Conversation');
        lines.push('');

        // Build full conversation
        activeTab.messages.forEach(msg => {
            if (msg.loading) return;
            if (msg.role === 'user') {
                lines.push(`### You asked:`);
                lines.push(`> ${msg.content}`);
                lines.push('');
            } else {
                lines.push(`### The Guide:`);
                lines.push(msg.content);
                lines.push('');
            }
        });

        lines.push('---');
        lines.push('');
        lines.push('*Generated by The Guide at Pleasance.app*');

        // Create and download file
        const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pleasance-chat-${new Date().toISOString().split('T')[0]}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="min-h-screen bg-charcoal flex">
            {/* Left: Character Panel */}
            <div className="hidden lg:flex w-80 flex-col items-center justify-center border-r border-wine-900/20 bg-gradient-to-b from-charcoal-950 to-charcoal-900 relative overflow-hidden">
                <GuideCharacter mood={characterMood} className="h-[70vh] max-h-[600px]" />

                {/* Character name plate */}
                <div className="absolute bottom-8 left-0 right-0 text-center">
                    <h2 className="font-serif text-xl text-gray-200 tracking-wide">The Guide</h2>
                    <p className="text-xs text-wine-400/60 italic capitalize">{characterMood}</p>
                </div>
            </div>

            {/* Right: Consultation Area */}
            <div className="flex-1 flex flex-col min-h-screen">
                {/* Header */}
                <div className="border-b border-wine-900/20 bg-charcoal-950/80 backdrop-blur sticky top-16 z-10">
                    <div className="max-w-3xl mx-auto px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="text-gray-500 hover:text-gray-300 transition-colors"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                </button>
                                <div className="flex items-center gap-3 lg:hidden">
                                    <GuideAvatar size="md" />
                                    <div>
                                        <h1 className="font-serif text-lg text-gray-100">The Guide</h1>
                                        <p className="text-xs text-gray-500 italic">Private consultation</p>
                                    </div>
                                </div>
                                <h1 className="hidden lg:block font-serif text-lg text-gray-100">Private Consultation</h1>
                            </div>
                            {/* Download transcript button */}
                            {hasMessages && (
                                <button
                                    onClick={downloadTranscript}
                                    className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-400 hover:text-wine-400 border border-wine-900/30 hover:border-wine-500/50 rounded transition-all"
                                    title="Download this conversation"
                                >
                                    <Download className="w-4 h-4" />
                                    <span className="hidden sm:inline">Save</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sessions */}
                <div className="border-b border-wine-900/10 bg-charcoal-900/30">
                    <div className="max-w-3xl mx-auto px-6">
                        <div className="flex items-center gap-1 py-2 overflow-x-auto text-xs">
                            {tabs.map((tab, i) => (
                                <div
                                    key={tab.id}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded cursor-pointer transition-all group
                                        ${tab.id === activeTabId ? 'text-wine-300 border-b-2 border-wine-500' : 'text-gray-500 hover:text-gray-300'}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <BookOpen className="w-3 h-3" />
                                    <span className="truncate max-w-[100px]">{tab.title === 'New Chat' ? `Session ${i + 1}` : tab.title}</span>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); closeTab(tab.id); }}
                                        className="opacity-0 group-hover:opacity-100 hover:text-wine-400"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                            <button onClick={createTab} className="px-2 py-1.5 text-gray-500 hover:text-wine-400" title="New session">
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto">
                    <div className="max-w-3xl mx-auto px-6 py-8">
                        {!hasMessages ? (
                            <div className="text-center py-12">
                                {/* Mobile: show character inline */}
                                <div className="lg:hidden flex justify-center mb-8">
                                    <GuideCharacter mood="welcome" className="h-48" />
                                </div>
                                <h2 className="font-serif text-2xl text-gray-200 mb-3">Welcome to the archives</h2>
                                <p className="text-gray-500 max-w-md mx-auto leading-relaxed mb-6">
                                    Share what you're curious about. This consultation is entirely private.
                                </p>

                                {/* Session disclaimer */}
                                <div className="bg-wine-950/30 border border-wine-900/30 rounded-lg px-4 py-3 max-w-md mx-auto mb-8">
                                    <p className="text-xs text-gray-500">
                                        <span className="text-wine-400 font-medium">Privacy note:</span> Chat history is stored locally in your browser only.
                                        It will not persist across different browser sessions or devices, and is automatically cleared after 1 hour of inactivity.
                                        <button
                                            onClick={downloadTranscript}
                                            className="text-wine-400 hover:text-wine-300 underline underline-offset-2 ml-1"
                                            disabled={!hasMessages}
                                        >
                                            Download your conversation
                                        </button>{' '}
                                        to keep a copy.
                                    </p>
                                </div>

                                <div className="flex flex-wrap gap-2 justify-center text-sm">
                                    {[
                                        "What's the difference between a kink and a fetish?",
                                        "How do I bring up something new with my partner?",
                                        "Explain aftercare",
                                        "What are safe words for?"
                                    ].map((q, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setInput(q)}
                                            className="px-4 py-2 border border-wine-900/30 hover:border-wine-500/50 text-gray-400 hover:text-gray-200 rounded transition-all"
                                        >
                                            {q}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {activeTab?.messages.map((msg, i) => (
                                    <div key={msg.id || i} className={msg.role === 'user' ? 'border-l-2 border-wine-500/30 pl-4' : ''}>
                                        {msg.role === 'user' ? (
                                            <div>
                                                <p className="text-xs text-wine-400/60 mb-1 uppercase tracking-wider">Your question</p>
                                                <p className="text-gray-200 italic">"{msg.content}"</p>
                                            </div>
                                        ) : (
                                            <div className="mt-4">
                                                {msg.loading ? (
                                                    <div className="flex items-center gap-3 text-gray-500 italic">
                                                        <Pulse>
                                                            <Feather className="w-4 h-4" />
                                                        </Pulse>
                                                        <span>Consulting the archives...</span>
                                                    </div>
                                                ) : (
                                                    <div className={`prose prose-sm prose-invert max-w-none ${msg.error ? 'text-red-400' : ''}`}>
                                                        <ReactMarkdown
                                                            components={{
                                                                p: ({ node, ...props }) => <p {...props} className="text-gray-300 leading-relaxed mb-3" />,
                                                                a: ({ node, ...props }) => (
                                                                    <a {...props} target="_blank" rel="noopener noreferrer" className="text-wine-400 hover:text-wine-300 underline underline-offset-2" />
                                                                ),
                                                                strong: ({ node, ...props }) => <strong {...props} className="text-gray-100 font-medium" />,
                                                                ul: ({ node, ...props }) => <ul {...props} className="list-disc list-inside text-gray-300 space-y-1" />,
                                                            }}
                                                        >
                                                            {msg.content}
                                                        </ReactMarkdown>
                                                        {msg.context && msg.context.kinksFound + msg.context.actionsFound > 0 && (
                                                            <p className="text-xs text-gray-600 mt-4 pt-3 border-t border-wine-900/20">
                                                                Referenced {msg.context.kinksFound + msg.context.actionsFound} entries
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </div>
                        )}
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="max-w-3xl mx-auto px-6 py-2 text-red-400/80 text-sm italic">{error}</div>
                )}

                {/* Input */}
                <div className="border-t border-wine-900/20 bg-charcoal-950/50 sticky bottom-0">
                    <div className="max-w-3xl mx-auto px-6 py-5">
                        <div className="relative">
                            <Feather className="absolute left-4 top-4 w-4 h-4 text-wine-500/40" />
                            <textarea
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Type your question and press Enter..."
                                className="w-full bg-transparent border border-wine-900/30 focus:border-wine-500/50 rounded-lg pl-12 pr-4 py-4 text-gray-200 placeholder-gray-600 resize-none focus:outline-none font-light"
                                rows={2}
                                disabled={isLoading}
                            />
                        </div>
                        <p className="text-xs text-gray-600 mt-2 text-center">Press Enter to submit</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ChatPage;
